import {CdkTextareaAutosize, TextFieldModule} from "@angular/cdk/text-field"
import {AfterViewInit, Component, ElementRef, afterNextRender, effect, input, viewChild, output, ChangeDetectionStrategy} from "@angular/core"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-native-input-text-area",
    templateUrl: "./native-input-text-area.component.html",
    styleUrls: ["./native-input-text-area.component.scss"],
    imports: [TextFieldModule],
})
export class NativeInputTextAreaComponent implements AfterViewInit {
    readonly $inputElement = viewChild.required<ElementRef>("inputField")
    readonly $placeholder = input<string>(undefined, {alias: "placeholder"})
    readonly $initialValue = input.required<string>({alias: "initialValue"})
    readonly $autoFocus = input(false, {alias: "autoFocus"})
    readonly valueChange = output<string>()
    readonly keyUp = output<string>()
    readonly $disabled = input(false, {alias: "disabled"})
    readonly $minRows = input(0, {alias: "minRows"})

    readonly $input = viewChild<ElementRef<HTMLTextAreaElement> | null>("inputField")
    readonly $cdkAutosize = viewChild(CdkTextareaAutosize)

    // Tracks the last value we set imperatively so we can detect user edits.
    private _appliedInitialValue: string | undefined = undefined

    constructor() {
        // Apply $initialValue to the DOM imperatively, but only when the user
        // has not changed the textarea content since the last time we set it.
        // This prevents the feedback loop caused by parents that update the
        // initialValue signal on every keyUp, which would otherwise reset the
        // textarea value (and cursor position) on every change-detection cycle.
        effect(() => {
            const init = this.$initialValue()
            const el = this.$input()?.nativeElement
            if (!el) return
            if (this._appliedInitialValue === undefined || el.value === this._appliedInitialValue) {
                el.value = init
                this._appliedInitialValue = init
            }
        })
        afterNextRender(() => {
            this.$cdkAutosize()?.resizeToFitContent(true)
        })
    }

    ngAfterViewInit(): void {
        if (this.$autoFocus()) {
            this.$inputElement().nativeElement.focus()
        }
    }

    onChange(_$event: Event) {
        const value = this.$inputElement().nativeElement.value
        this.valueChange.emit(value)
    }

    onKeyUp(_$event: KeyboardEvent) {
        this.keyUp.emit(this.$inputElement().nativeElement.value!)
    }

    updateValue(value: string) {
        const inputValue = this.$input()
        if (inputValue) {
            inputValue.nativeElement.value = value
        }
        this.valueChange.emit(value)
    }

    focus() {
        const inputValue = this.$input()
        if (inputValue) {
            inputValue.nativeElement.focus()
        }
    }
}
